import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Star, Clock, Flame } from 'lucide-react';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #000000 0%, #333333 100%);
  color: white;
  padding: 3rem 0;
  text-align: center;
  border-radius: 0 0 20px 20px;
  margin-bottom: 3rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const CategoryTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const CategoryTab = styled.button<{ active: boolean }>`
  background: ${props => props.active ? '#FFD700' : 'white'};
  color: ${props => props.active ? '#000000' : '#000000'};
  border: 2px solid #FFD700;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#FFED4E' : '#f0f0f0'};
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const MenuItem = styled(motion.div)`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
    border-color: #FFD700;
  }
`;

const MenuItemImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #000000 0%, #333333 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="%23FFD700" opacity="0.3"/><circle cx="20" cy="30" r="1.5" fill="%23FFD700" opacity="0.2"/><circle cx="80" cy="70" r="1.5" fill="%23FFD700" opacity="0.2"/></svg>');
    opacity: 0.5;
  }
`;

const MenuItemContent = styled.div`
  padding: 1.5rem;
`;

const MenuItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const MenuItemTitle = styled.h3`
  color: #2c3e50;
  font-size: 1.3rem;
  margin: 0;
`;

const MenuItemPrice = styled.span`
  color: #FFD700;
  font-weight: bold;
  font-size: 1.2rem;
`;

const MenuItemDescription = styled.p`
  color: #7f8c8d;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const MenuItemTags = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Tag = styled.span<{ type: 'spicy' | 'popular' | 'vegetarian' }>`
  background: ${props => {
    switch (props.type) {
      case 'spicy': return '#e74c3c';
      case 'popular': return '#f39c12';
      case 'vegetarian': return '#27ae60';
      default: return '#95a5a6';
    }
  }};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PreparationTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

interface MenuItem {
  id: number;
  name: string;
  description: string;
  price: number;
  category: string;
  image: string;
  preparationTime: string;
  tags: ('spicy' | 'popular' | 'vegetarian')[];
}

const menuItems: MenuItem[] = [
  {
    id: 1,
    name: "Poule & Poulette Special",
    description: "Onze signature kipfilet, gemarineerd in speciale kruiden en gegrild tot perfectie. Geserveerd met verse groenten en aardappelpuree.",
    price: 24.50,
    category: "hoofdgerechten",
    image: "🍗",
    preparationTime: "20 min",
    tags: ["popular"]
  },
  {
    id: 2,
    name: "Spicy Poulette Wings",
    description: "Krokante kippenvleugels in onze speciale spicy marinade. Perfect als voorgerecht of snack.",
    price: 16.90,
    category: "voorgerechten",
    image: "🍗",
    preparationTime: "15 min",
    tags: ["spicy", "popular"]
  },
  {
    id: 3,
    name: "Poulette Burger",
    description: "Gegrilde kipfilet op een vers gebakken broodje met sla, tomaat, ui en onze speciale saus.",
    price: 18.50,
    category: "burgers",
    image: "🍔",
    preparationTime: "12 min",
    tags: ["popular"]
  },
  {
    id: 4,
    name: "Kip Caesar Salade",
    description: "Verse sla met gegrilde kipfilet, parmezaanse kaas, croutons en caesar dressing.",
    price: 19.90,
    category: "salades",
    image: "🥗",
    preparationTime: "10 min",
    tags: ["vegetarian"]
  },
  {
    id: 5,
    name: "Poulette Curry",
    description: "Milde curry met kipfilet, kokosmelk en verse groenten. Geserveerd met basmatirijst.",
    price: 22.50,
    category: "hoofdgerechten",
    image: "🍛",
    preparationTime: "25 min",
    tags: ["spicy"]
  },
  {
    id: 6,
    name: "Kip Saté",
    description: "Gegrilde kipspiesjes met pindasaus, geserveerd met rijst en groenten.",
    price: 21.90,
    category: "hoofdgerechten",
    image: "🍢",
    preparationTime: "18 min",
    tags: ["popular"]
  },
  {
    id: 7,
    name: "Poulette Soep",
    description: "Huisgemaakte kippensoep met verse groenten en noedels.",
    price: 8.90,
    category: "voorgerechten",
    image: "🍲",
    preparationTime: "8 min",
    tags: []
  },
  {
    id: 8,
    name: "Veggie Poulette",
    description: "Vegetarische versie van onze special met seizoensgroenten en quinoa.",
    price: 20.50,
    category: "hoofdgerechten",
    image: "🥬",
    preparationTime: "22 min",
    tags: ["vegetarian"]
  },
  {
    id: 9,
    name: "Kip Nuggets",
    description: "Krokante kipnuggets met zoetzure saus. Perfect voor kinderen.",
    price: 14.90,
    category: "kinderen",
    image: "🍗",
    preparationTime: "10 min",
    tags: []
  },
  {
    id: 10,
    name: "Poulette Dessert",
    description: "Huisgemaakte tiramisu met een twist van koffie en amaretto.",
    price: 9.50,
    category: "desserts",
    image: "🍰",
    preparationTime: "5 min",
    tags: []
  }
];

const categories = [
  { id: 'all', name: 'Alles' },
  { id: 'voorgerechten', name: 'Voorgerechten' },
  { id: 'hoofdgerechten', name: 'Hoofdgerechten' },
  { id: 'burgers', name: 'Burgers' },
  { id: 'salades', name: 'Salades' },
  { id: 'kinderen', name: 'Kinderen' },
  { id: 'desserts', name: 'Desserts' }
];

const MenuPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredItems = selectedCategory === 'all' 
    ? menuItems 
    : menuItems.filter(item => item.category === selectedCategory);

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Onze Menu</HeroTitle>
        <HeroSubtitle>
          Ontdek onze heerlijke selectie van kipgerechten en meer
        </HeroSubtitle>
      </HeroSection>

      <CategoryTabs>
        {categories.map((category) => (
          <CategoryTab
            key={category.id}
            active={selectedCategory === category.id}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.name}
          </CategoryTab>
        ))}
      </CategoryTabs>

      <MenuGrid>
        {filteredItems.map((item, index) => (
          <MenuItem
            key={item.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
          >
            <MenuItemImage>
              {item.image}
            </MenuItemImage>
            <MenuItemContent>
              <MenuItemHeader>
                <MenuItemTitle>{item.name}</MenuItemTitle>
                <MenuItemPrice>€{item.price.toFixed(2)}</MenuItemPrice>
              </MenuItemHeader>
              
              <MenuItemDescription>
                {item.description}
              </MenuItemDescription>
              
              <MenuItemTags>
                {item.tags.includes('spicy') && (
                  <Tag type="spicy">
                    <Flame size={12} />
                    Spicy
                  </Tag>
                )}
                {item.tags.includes('popular') && (
                  <Tag type="popular">
                    <Star size={12} />
                    Populair
                  </Tag>
                )}
                {item.tags.includes('vegetarian') && (
                  <Tag type="vegetarian">
                    🌱 Vegetarisch
                  </Tag>
                )}
              </MenuItemTags>
              
              <PreparationTime>
                <Clock size={14} />
                Bereidingstijd: {item.preparationTime}
              </PreparationTime>
            </MenuItemContent>
          </MenuItem>
        ))}
      </MenuGrid>
    </PageContainer>
  );
};

export default MenuPage;
